import moment from "moment";
import { createStructuredSelector, createSelector } from "reselect";
import { workareaSelector } from "features/workarea/selectors";
import { organizationSelector } from "features/organization/selectors";
import { soloLookupSelector } from "../../../app/soloLookup/selectors";

export const soloFilesFetchingSelector = state =>
  state.solo.getIn(["annotate", "soloFilesFetching"]);
export const soloFilesDidInvalidateSelector = state =>
  state.solo.getIn(["annotate", "soloFilesDidInvalidate"]);

export const soloFilesBeingSaved = state =>
  state.solo.getIn(["annotate", "soloFilesBeingSaved"]);
export const soloFilesBeingSavedSelector = createSelector(
  soloFilesBeingSaved,
  x => x.toJS()
);
export const soloSavingFilesSelector = createSelector(
  soloFilesBeingSaved,
  x => !x.isEmpty()
);

export const soloSuccessfulAnnotations = state =>
  state.solo.getIn(["annotate", "soloSuccessfulAnnotations"]);

export const soloSuccessfulAnnotationsSelector = createSelector(
  soloSuccessfulAnnotations,
  x => x.toJS()
);

export const soloFilesToAnnotate = state =>
  state.solo.getIn(["annotate", "soloFilesToAnnotate"]);
export const soloFilesToAnnotateSelector = createSelector(
  soloFilesToAnnotate,
  x => x.toJS()
);
export const soloFilesListSelector = createSelector(soloFilesToAnnotate, x =>
  x
    .toList()
    .sortBy(a => moment(a.get("collectionDt")))
    .reverse()
    .toJS()
);

export const soloWantedFilePks = state =>
  state.solo.getIn(["annotate", "soloFilesBeingAnnotated"]).toSet();
export const soloFilesBeingAnnotatedSelector = createSelector(
  soloWantedFilePks,
  x => x.toJS()
);

export const soloFetchedFilePks = state =>
  state.solo
    .getIn(["annotate", "soloCastsInFiles"])
    .keySeq()
    .map(x => parseInt(x, 10))
    .toSet();
export const soloFetchedFilePksSelector = createSelector(
  soloFetchedFilePks,
  x => x.toJS()
);

export const soloFetchingFilePks = createSelector(
  [soloWantedFilePks, soloFetchedFilePks],
  (w, f) => w.subtract(f)
);
export const soloFetchingFilePksSelector = createSelector(
  soloFetchingFilePks,
  x => x.toJS()
);

export const soloFetchedAndWantedPks = createSelector(
  [soloWantedFilePks, soloFetchedFilePks],
  (w, f) => w.intersect(f)
);
export const soloFetchedFilesBeingAnnotatedSelector = createSelector(
  soloFetchedAndWantedPks,
  x => x.toJS()
);

export const soloCastsInFiles = state =>
  state.solo.getIn(["annotate", "soloCastsInFiles"]);
export const soloCastsInFilesSelector = createSelector(soloCastsInFiles, x =>
  x.toJS()
);

export const soloCastsInFilesBeingAnnotatedSelector = createSelector(
  [soloFetchedFilesBeingAnnotatedSelector, soloCastsInFilesSelector],
  (fetched, casts) =>
    fetched
      // only keep the fetched filePks that have casts
      .filter(filePk => casts[filePk])
      // for each file, add the casts to the array as objects indexed by castpk
      .reduce((R, x) => {
        Object.keys(casts[x]).forEach(y => {
          const cast = {};
          cast[y] = casts[x][y];
          R.push(cast);
        });
        return R;
      }, [])
      // sort the casts based on their collection time
      .sort(
        (a, b) =>
          moment(a[Object.keys(a)[0]].startDt) -
          moment(b[Object.keys(b)[0]].startDt)
      )
      // return an object indexed by castpk
      /* eslint-disable no-param-reassign */
      .reduce((R, x) => {
        const key = Object.keys(x)[0];
        R[key] = x[key];
        return R;
      }, {})
);
/* eslint-enable no-param-reassign */

export const soloCastsInFilesBeingAnnotatedListSelector = createSelector(
  [soloFetchedFilesBeingAnnotatedSelector, soloCastsInFilesSelector],
  (fetched, casts) =>
    fetched
      .reduce((R, x) => {
        Object.keys(casts[x]).forEach(y => {
          R.push(casts[x][y]);
        });
        return R;
      }, [])
      .sort((a, b) => moment(a.startDt) - moment(b.startDt))
);

export const soloCastsFetchingSelector = createSelector(
  soloFetchingFilePksSelector,
  x => x.length > 0
);

export const soloMatchedFormsSelector = createSelector(
  state => state.solo.getIn(["annotate", "soloMatchedForms"]),
  x => {
    const matchedForms = x.toJS();
    // remove entries with null values, can happen in no forms are matched
    Object.keys(matchedForms).forEach(
      k => matchedForms[k] == null && delete matchedForms[k]
    );
    return matchedForms;
  }
);
export const soloMatchedFormsBeingAnnotatedSelector = createSelector(
  [soloCastsInFilesBeingAnnotatedSelector, soloMatchedFormsSelector],
  (casts, allForms) => {
    const castPks = Object.keys(casts);
    return Object.keys(allForms)
      .filter(key => castPks.includes(key))
      .reduce((obj, key) => {
        obj[key] = allForms[key]; // eslint-disable-line no-param-reassign
        return obj;
      }, {});
  }
);

export const soloDuplicateFormsInCastsBeingAnnotatedSelector = createSelector(
  [
    soloMatchedFormsBeingAnnotatedSelector,
    soloCastsInFilesBeingAnnotatedSelector
  ],
  (m, c) => {
    const forms = [];
    const duplicates = [];

    Object.keys(m).forEach(x => {
      forms.push(m[x]);
    });

    if (forms.length === 0) {
      return duplicates;
    }
    forms.forEach(f => {
      const pk = f.soloCastPk;
      const id = f.hakaiSampleId;
      if (!c[pk].miscast) {
        const sameForm = forms.filter(
          x => !c[x.soloCastPk].miscast && x.hakaiSampleId === id
        );
        if (sameForm.length > 1) duplicates.push(pk);
      }
    });

    return duplicates;
  }
);
export const possibleFormsSelector = createSelector(
  state => state.solo.getIn(["annotate", "possibleForms"]),
  x => x.toJS()
);

export const soloAnnotationModalOpenSelector = state =>
  state.solo.getIn(["annotate", "soloAnnotationModalOpen"]);
export const soloAnnotationModalSelectedSelector = state =>
  state.solo.getIn(["annotate", "soloAnnotationModalSelected"]);
export const soloAnnotationModalCastSelector = createSelector(
  state => state.solo.getIn(["annotate", "soloAnnotationModalCast"]),
  x => x.toJS()
);
export const soloAnnotationModalFormsSelector = createSelector(
  state => state.solo.getIn(["annotate", "soloAnnotationModalForms"]),
  x => x.toJS()
);
export const soloChartModalOpenSelector = state =>
  state.solo.getIn(["annotate", "soloChartModalOpen"]);
export const soloChartModalCastSelector = createSelector(
  state => state.solo.getIn(["annotate", "soloChartModalCast"]),
  x => x.toJS()
);
export const soloChartDataFetchingSelector = state =>
  state.solo.getIn(["annotate", "soloChartDataFetching"]);
export const soloChartDataSelector = createSelector(
  state => state.solo.getIn(["annotate", "soloChartData"]),
  x => x.toJS()
);

export const soloAnnotationSnackbarOpenSelector = state =>
  state.solo.getIn(["annotate", "soloAnnotationSnackbarOpen"]);

export default createStructuredSelector({
  soloFilesFetching: soloFilesFetchingSelector,
  soloCastsFetching: soloCastsFetchingSelector,
  soloFetchedFilePks: soloFetchedFilePksSelector,
  soloSavingFiles: soloSavingFilesSelector,
  soloFilesDidInvalidate: soloFilesDidInvalidateSelector,
  soloFilesToAnnotate: soloFilesToAnnotateSelector,
  soloFilesList: soloFilesListSelector,
  soloFilesBeingAnnotated: soloFilesBeingAnnotatedSelector,
  soloCastsInFiles: soloCastsInFilesSelector,
  soloCastsInFilesBeingAnnotated: soloCastsInFilesBeingAnnotatedSelector,
  soloCastsInFilesBeingAnnotatedList: soloCastsInFilesBeingAnnotatedListSelector,
  soloMatchedForms: soloMatchedFormsSelector,
  soloMatchedFormsBeingAnnotated: soloMatchedFormsBeingAnnotatedSelector,
  soloDuplicateFormsInCastsBeingAnnotated: soloDuplicateFormsInCastsBeingAnnotatedSelector,
  possibleForms: possibleFormsSelector,
  soloAnnotationModalOpen: soloAnnotationModalOpenSelector,
  soloAnnotationModalCast: soloAnnotationModalCastSelector,
  soloAnnotationModalForms: soloAnnotationModalFormsSelector,
  soloAnnotationModalSelected: soloAnnotationModalSelectedSelector,
  soloChartModalOpen: soloChartModalOpenSelector,
  soloChartModalCast: soloChartModalCastSelector,
  soloChartDataFetching: soloChartDataFetchingSelector,
  soloChartData: soloChartDataSelector,
  soloAnnotationSnackbarOpen: soloAnnotationSnackbarOpenSelector,
  soloSuccessfulAnnotations: soloSuccessfulAnnotationsSelector,
  workarea: workareaSelector,
  organization: organizationSelector,
  soloUIDLookup: soloLookupSelector
});
